package com.evoapps.evoshift.domain.market.payroll

import com.evoapps.evoshift.domain.market.Market
import com.evoapps.evoshift.domain.market.payroll.poland.PolandPayroll
import com.evoapps.evoshift.domain.model.SystemContractType
import kotlinx.datetime.DayOfWeek
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime

/** The labour-cost and net-pay rules of one market. Pure: no I/O, the same input gives the same result. */
interface PayrollRules {
    val market: Market

    /**
     * SH-212: the payroll facts that are income tax reliefs in this market, in the order the
     * employee timeline offers them (its "Tax relief" lane). None: the lane is not offered.
     */
    val taxReliefs: List<PayrollFact> get() = emptyList()

    /** Working hours of a full-time employee in the month (the base for monthly salaries and allowances). */
    fun workingHours(year: Int, month: Int): Int

    /** Full-time working hours from [start] to [endInclusive], both included: part of a month (SH-48). */
    fun workingHours(start: LocalDate, endInclusive: LocalDate): Int

    /** Hours of [start, end) that fall in the workplace's night period. */
    fun nightHours(start: LocalDateTime, end: LocalDateTime, settings: WorkplacePayrollSettings): Double

    /** Whether the day is a public holiday in this market. */
    fun isHoliday(date: LocalDate): Boolean

    /** Gross pay and employer cost of one shift. */
    fun shiftPay(input: ShiftPayInput): ShiftPay

    /** Contributions, tax, net pay and employer cost of one month's gross. */
    fun payslip(input: PayslipInput): Payslip

    /**
     * SH-149: [payslip] for a month in which the person's facts changed (a status that started or
     * ended, PIT-2 handed in): each set of facts in [shares] gives the whole month's payslip, and
     * the lines are weighted by the pay earned under each. One set of facts: the plain payslip with
     * it; none: with [PayslipInput.profile]. An estimate: the accountant applies the reliefs by the
     * month the pay is paid in and the contributions by the days worked.
     */
    fun payslip(input: PayslipInput, shares: List<ProfileShare>): Payslip {
        val parts = shares.filter { it.weight > 0 }
            .groupBy { it.profile.copy(under26Until = null, studentUntil = null) }
            .map { (profile, list) -> profile to list.sumOf { it.weight } }
        if (parts.size <= 1) return payslip(input.copy(profile = parts.firstOrNull()?.first ?: input.profile))
        val total = parts.sumOf { it.second }
        val slips = parts.map { (profile, weight) -> payslip(input.copy(profile = profile)) to weight / total }
        fun cents(value: Double) = kotlin.math.round(value * 100) / 100.0
        fun blend(lines: (Payslip) -> List<PayLine>): List<PayLine> =
            slips.flatMap { (slip, _) -> lines(slip).map { it.kind } }.distinct().map { kind ->
                PayLine(kind, cents(slips.sumOf { (slip, share) -> lines(slip).filter { it.kind == kind }.sumOf { it.amount } * share }))
            }
        val employee = blend { it.employeeLines }
        val employer = blend { it.employerLines }
        val gross = slips.first().first.gross
        return Payslip(
            gross = gross,
            employeeLines = employee,
            employerLines = employer,
            net = cents(gross - employee.sumOf { it.amount }),
            employerCost = cents(slips.sumOf { (slip, share) -> slip.employerCost * share }),
            notes = slips.flatMap { it.first.notes }.toSet()
        )
    }

    /** SH-57: overtime, Sunday and holiday work and the minimum wage of one person's month. */
    fun monthFactors(input: MonthWorkInput): MonthPayFactors

    /**
     * SH-113: the full-time annual leave of [year] in days for a person with [contract] and
     * [profile] (seniority), before pro rata; null: the contract carries no statutory leave.
     */
    fun annualLeaveDays(contract: SystemContractType, profile: PayrollProfile, year: Int): Int?

    /** SH-113: of the annual leave, the days a person may take on demand in [year] (0: none). */
    fun onDemandLeaveDays(year: Int): Int

    /** SH-113: whether [date] counts as a day of leave: a working day, not a weekend or holiday. */
    fun isLeaveDay(date: LocalDate): Boolean =
        date.dayOfWeek != DayOfWeek.SATURDAY && date.dayOfWeek != DayOfWeek.SUNDAY && !isHoliday(date)

    companion object {
        fun of(market: Market): PayrollRules = when (market) {
            Market.POLAND -> PolandPayroll
        }
    }
}
